import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Check, Crown, Briefcase, TrendingUp, Landmark, Building2, Sparkles,
  LayoutDashboard, Wand2, Bot, Telescope, Network, FlaskConical, Globe2, Brain, BookOpen,
  Calculator, Users, Mic, Handshake, Receipt, Lock, Layers, Package, ShieldCheck, Zap,
  Presentation, FileSignature, Settings,
} from "lucide-react";

const SECTIONS = [
  {
    label: "Core",
    color: "#D4AF37",
    items: [
      { icon: LayoutDashboard, name: "Command", desc: "Executive dashboard: KPIs, alerts, decision queue" },
      { icon: Wand2, name: "Copilot", desc: "Conversational AI that knows your institution" },
      { icon: Bot, name: "Agents", desc: "7 autonomous agents proposing approval-gated actions" },
      { icon: Sparkles, name: "AI Nexus", desc: "10 specialised AI workspaces for drafting & synthesis" },
      { icon: Telescope, name: "Research", desc: "Deep web research with citations" },
    ],
  },
  {
    label: "Intelligence",
    color: "#3B82F6",
    items: [
      { icon: Network, name: "Graph", desc: "Relationship map of stakeholders & projects" },
      { icon: FlaskConical, name: "Simulation", desc: "Scenario modelling for strategic decisions" },
      { icon: Globe2, name: "Africa", desc: "Sovereign data layer for African economies & DFIs" },
      { icon: Brain, name: "Memory", desc: "Institutional memory that survives transitions" },
      { icon: BookOpen, name: "Knowledge", desc: "Document library with AI Q&A" },
      { icon: Calculator, name: "Finance", desc: "Capital stack & projection modelling" },
    ],
  },
  {
    label: "Engagement",
    color: "#22C55E",
    items: [
      { icon: Users, name: "Relationships", desc: "Multi-tenant CRM for govt, investor, executive segments" },
      { icon: Landmark, name: "Engagement Hub", desc: "AI-generated government & investor briefings" },
      { icon: Mic, name: "Meetings", desc: "Recording → transcription → action items" },
      { icon: Handshake, name: "Diplomacy", desc: "5 protocol templates + influence-path discovery" },
      { icon: Crown, name: "Reputation", desc: "Track standing across institutions & sectors" },
    ],
  },
  {
    label: "Execution",
    color: "#F59E0B",
    items: [
      { icon: Briefcase, name: "Operations", desc: "Projects, tasks, PPP workflow support" },
      { icon: FileSignature, name: "Proposals", desc: "AI-generated, e-signature ready, GHS-priced" },
      { icon: Presentation, name: "Studio", desc: "Board-grade presentations, PPTX export" },
      { icon: Zap, name: "Automation", desc: "Trigger-based workflow automation" },
      { icon: Receipt, name: "FinOps", desc: "Invoices, payments, procurement, budgets" },
      { icon: Lock, name: "Vault", desc: "Secure documents: versioning, expiry, audit-logged" },
    ],
  },
  {
    label: "System",
    color: "#A855F7",
    items: [
      { icon: Layers, name: "Entities", desc: "Multi-entity hierarchy: subsidiaries, JVs, SPVs, funds" },
      { icon: Package, name: "Marketplace", desc: "Institutional packs that materialise real assets" },
      { icon: Users, name: "Team", desc: "Seats, invites, role assignment" },
      { icon: Globe2, name: "Platform", desc: "System status, integrations, API health" },
      { icon: ShieldCheck, name: "Governance", desc: "Audit logs, compliance, access controls" },
    ],
  },
];

const AUDIENCES = [
  { icon: Crown, title: "CEOs & Executive Offices", desc: "A single command surface. Weekly briefings. Decision queue. Institutional memory across leadership transitions." },
  { icon: Briefcase, title: "Consulting & Advisory Firms", desc: "Client engagement workflows, Studio for board-grade decks, multi-client tenant isolation, AI-drafted advisory artefacts." },
  { icon: Landmark, title: "Government Ministries & Agencies", desc: "PPP project lifecycle, contract-based deployment (private cloud / on-prem), unsolicited bid pipeline, compliance calendars (BoG, SEC, GRA, NPRA, DPC)." },
  { icon: TrendingUp, title: "Investors, DFIs, Family Offices & Funds", desc: "Capital pipeline, due diligence dossiers, investor briefings, scenario modelling, portfolio rollup across entities." },
  { icon: Building2, title: "Project Sponsors & Infrastructure Developers", desc: "PPP automation suite, financial-close checklists, government counterparty playbooks, risk allocation matrices." },
  { icon: FileSignature, title: "NGOs & Development Implementers", desc: "Grant Writing Suite (USAID / FCDO / GIZ / AFD templates), logframe builders, M&E plans, donor reporting." },
  { icon: ShieldCheck, title: "Regulated Entities & Listed Companies", desc: "Compliance Pack, audit-ready Vault, board reporting cadence, governance discipline." },
  { icon: Users, title: "Boards & Family Offices", desc: "Quarterly board reports, executive decision logs, risk dashboards, CEO 1-page weekly briefings." },
];

const BENEFITS = [
  { n: "01", title: "One surface instead of fifteen tools", desc: "Pandora Nexus replaces the entire stack with a single coherent system where stakeholders, projects, proposals, contracts, AI insights and financial operations talk to each other natively." },
  { n: "02", title: "AI that knows your institution", desc: "Copilot, Agents and AI Nexus operate on your stakeholders, your proposals, your governance data and your historical decisions — with org-level isolation. Insights are yours." },
  { n: "03", title: "Approval-gated autonomy", desc: "Seven autonomous agents proactively scan your operations and propose actions. Nothing executes without your approval — the productivity of an army of analysts with the safety of a board-approved process." },
  { n: "04", title: "Audit-grade compliance by default", desc: "Every action logged. Every contract versioned. Every document tiered for confidentiality. Every API call org-scoped. Built for regulators, auditors and boards — not retrofitted." },
  { n: "05", title: "Made for Africa, ready for the world", desc: "Currency in GHS. Workflows for the Ghana PPP Act 2020, AfCFTA, BoG/SEC/GRA/NPRA compliance, MoFA & COCOBOD engagement. Built once for a market historically served by tools designed elsewhere." },
  { n: "06", title: "Institutional memory that survives transitions", desc: "When a CEO leaves, a minister rotates, a partner moves on — the rationale, the relationships, the decisions and the open threads remain. Pandora Nexus is the corporate brain that doesn't forget." },
  { n: "07", title: "Multi-tenant by architecture", desc: "A consulting firm can run dozens of clients, each isolated. A holding company can run multiple subsidiaries. A government can run multiple agencies. Data isolation enforced at the database layer — not the UI." },
  { n: "08", title: "Time-to-value in days, not months", desc: "Curated Marketplace packs install in seconds and materialise real templates, automations, knowledge and AI prompts directly into your workspace. You never start from a blank page." },
  { n: "09", title: "Designed like a luxury product", desc: "Dark glassmorphism, gold accents, zero AI-slop aesthetic. Built for executives who will spend hours in the system daily. Apple Vision Pro × Bloomberg Terminal, made for institutional use." },
  { n: "10", title: "Built by people who do this work", desc: "Pandora Management Consult Ltd is itself a Ghanaian executive consulting firm. Every workflow is shaped by real ministerial briefings, real PPP submissions, real board packs — not by engineers guessing." },
];

const TIERS = [
  { name: "Enterprise Starter", audience: "Growing SMEs", price: "GH₵ 4,800 / mo", color: "#22C55E" },
  { name: "Enterprise Professional", audience: "Mid-market enterprises", price: "GH₵ 9,600 / mo", color: "#22C55E" },
  { name: "Enterprise Executive", audience: "Corporate groups, executive offices", price: "GH₵ 19,200 / mo", color: "#22C55E", star: true },
  { name: "Advisory Suite", audience: "Consulting firms", price: "GH₵ 24,000 / mo", color: "#A855F7" },
  { name: "Investor Suite", audience: "DFIs, capital firms, funds", price: "GH₵ 80,000 / mo", color: "#D4AF37" },
  { name: "Government Suite", audience: "Ministries, agencies, national projects", price: "GH₵ 400K – 16M+ / yr", color: "#3B82F6", contract: true },
];

export default function About() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="about-page">
      {/* Decorative orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-[-15%] left-[15%] w-[700px] h-[700px] rounded-full bg-[#D4AF37] opacity-[0.05] blur-[140px]" />
        <div className="absolute top-[40%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#00F0FF] opacity-[0.03] blur-[130px]" />
        <div className="absolute bottom-[-20%] left-[30%] w-[500px] h-[500px] rounded-full bg-[#3B82F6] opacity-[0.04] blur-[120px]" />
      </div>

      {/* Header */}
      <header className="border-b border-white/10 nexus-glass px-6 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-30" data-testid="about-header">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")} data-testid="about-logo">
          <img
            src="/pandora-nexus-mark.png"
            alt="Pandora Nexus"
            className="w-9 h-9 object-contain"
            style={{ filter: "drop-shadow(0 0 10px rgba(212,175,55,0.35))" }}
          />
          <div>
            <div className="font-display text-lg text-white leading-none">
              Pandora<span style={{ color: "var(--gold-500)" }}>Nexus</span>
            </div>
            <div className="label-eyebrow mt-1">Total Institutional Intelligence</div>
          </div>
        </div>
        <nav className="flex items-center gap-2 lg:gap-4">
          <Link to="/about" data-testid="nav-about" className="px-3 py-2 text-[11px] font-mono tracking-widest text-[#D4AF37]">ABOUT</Link>
          <Link to="/pricing" data-testid="nav-pricing" className="px-3 py-2 text-[11px] font-mono tracking-widest text-slate-300 hover:text-white">PRICING</Link>
          {user ? (
            <button onClick={() => navigate("/")} data-testid="nav-command" className="px-4 py-2 text-[11px] font-mono tracking-widest text-slate-300 hover:text-white">
              COMMAND CENTER →
            </button>
          ) : (
            <button onClick={() => navigate("/login")} data-testid="nav-signin" className="px-4 py-2 text-[11px] font-mono tracking-widest text-slate-300 hover:text-[#D4AF37]">
              SIGN IN →
            </button>
          )}
        </nav>
      </header>

      <main className="px-6 lg:px-8 py-16 lg:py-24 max-w-7xl mx-auto">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-24 lg:mb-32"
          data-testid="about-hero"
        >
          <div className="label-eyebrow mb-5">// About Pandora Nexus</div>
          <h1 className="font-display text-5xl lg:text-7xl text-white tracking-tight leading-[1.05] max-w-5xl">
            One platform. <br />
            <span style={{ color: "var(--gold-500)" }}>Total institutional</span> intelligence.
          </h1>
          <p className="text-slate-300 mt-8 text-lg lg:text-xl leading-relaxed max-w-3xl">
            The AI-powered institutional operating system for organisations that engage governments, raise capital,
            run multi-entity portfolios, and operate at the intersection of strategy, policy and execution.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate("/pricing")}
              data-testid="hero-pricing-cta"
              className="px-6 py-3 font-medium text-sm flex items-center gap-2 transition-all hover:opacity-90"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              See pricing <ArrowUpRight className="w-4 h-4" />
            </button>
            {!user && (
              <button
                onClick={() => navigate("/login")}
                data-testid="hero-signin-cta"
                className="px-6 py-3 font-medium text-sm border border-white/15 text-slate-200 hover:border-[#D4AF37]/60 hover:text-white transition-all"
              >
                Sign in to the platform
              </button>
            )}
          </div>
        </motion.section>

        {/* What it is */}
        <Section eyebrow="// What it is" title="An institutional operating system — not another SaaS tool.">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
            <div className="lg:col-span-2 nexus-glass-panel p-8 lg:p-10">
              <p className="text-slate-200 text-base lg:text-[17px] leading-relaxed">
                Pandora Nexus is built by <span className="text-[#D4AF37]">Pandora Management Consult Ltd</span> — a
                Ghanaian executive consulting firm — and engineered to become <em>Africa's AI-powered institutional
                intelligence and operational infrastructure ecosystem</em>. Palantir-grade depth, Apple-grade craft,
                made for African institutions and the partners who work with them.
              </p>
              <p className="text-slate-300 text-base mt-5 leading-relaxed">
                It is not a CRM. It is not a project tool. It is the single command surface that unifies executive
                decisions, stakeholder relationships, proposals, intelligence, financial operations, governance,
                contracts and autonomous AI agents — under one tenant-isolated, audit-logged roof.
              </p>
            </div>
            <div className="nexus-card p-6 lg:p-8 flex flex-col justify-between">
              <div>
                <div className="label-eyebrow text-[#D4AF37]">// The promise</div>
                <p className="font-display text-2xl text-white mt-3 leading-snug">
                  Your institutional operations should match the quality of your ambitions.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-3 text-center">
                <Stat n="28" label="Modules" />
                <Stat n="7" label="AI Agents" />
                <Stat n="6" label="Suites" />
              </div>
            </div>
          </div>
        </Section>

        {/* Modules — 5 layers */}
        <Section eyebrow="// What it does" title="28 modules organised into five operating layers.">
          <div className="space-y-10 mt-10">
            {SECTIONS.map((sec, i) => (
              <motion.div
                key={sec.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`layer-${sec.label.toLowerCase()}`}
              >
                <div className="flex items-baseline gap-4 mb-5">
                  <div className="w-2 h-2" style={{ background: sec.color }} />
                  <div className="label-eyebrow" style={{ color: sec.color }}>// {sec.label}</div>
                  <div className="font-mono text-[10px] text-slate-600 tracking-widest">· {sec.items.length} MODULES</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sec.items.map((it) => (
                    <div key={it.name} className="nexus-card p-4 flex items-start gap-3">
                      <div
                        className="w-9 h-9 flex items-center justify-center shrink-0"
                        style={{ background: sec.color + "1A", border: `1px solid ${sec.color}33` }}
                      >
                        <it.icon className="w-4 h-4" style={{ color: sec.color }} strokeWidth={1.6} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-sm text-white">{it.name}</div>
                        <div className="text-[12px] text-slate-400 leading-snug mt-0.5">{it.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Who it's for */}
        <Section eyebrow="// Who it's for" title="Built for institutions that cannot afford to operate informally.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {AUDIENCES.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="nexus-glass-panel p-6 flex items-start gap-4"
                data-testid={`audience-${i}`}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center shrink-0"
                  style={{ background: "rgba(212,175,55,0.10)", border: "1px solid rgba(212,175,55,0.30)" }}
                >
                  <a.icon className="w-5 h-5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-lg text-white">{a.title}</h3>
                  <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{a.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-slate-300 text-base mt-10 max-w-3xl leading-relaxed">
            In short — <span className="text-[#FCE588]">if your work requires you to be credible to a minister, a fund
            LP, a regulator, or a board, Pandora Nexus is your operating layer.</span>
          </p>
        </Section>

        {/* Benefits */}
        <Section eyebrow="// Why institutions choose Pandora Nexus" title="Ten reasons. Each one earned.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-10">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.n}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="nexus-card p-6 flex gap-5"
                data-testid={`benefit-${b.n}`}
              >
                <div className="font-display text-3xl shrink-0" style={{ color: "var(--gold-500)" }}>{b.n}</div>
                <div className="min-w-0">
                  <h3 className="font-display text-base text-white leading-snug">{b.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Pricing summary */}
        <Section eyebrow="// Pricing at a glance" title="Six suites. Paid in Ghana Cedis. Paystack-powered.">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
            {TIERS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className={`nexus-card p-5 relative ${t.star ? "border-[#D4AF37]/40" : ""}`}
                data-testid={`tier-${i}`}
              >
                {t.star && (
                  <div className="absolute -top-2 left-5 px-2 py-0.5 text-[9px] font-mono tracking-widest" style={{ background: "var(--gold-500)", color: "#050B14" }}>
                    POPULAR
                  </div>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5" style={{ background: t.color }} />
                  <span className="label-eyebrow">{t.contract ? "Contract" : "Subscription"}</span>
                </div>
                <h3 className="font-display text-lg text-white">{t.name}</h3>
                <p className="text-[12px] text-slate-500 mt-1 leading-snug">{t.audience}</p>
                <div className="mt-4 font-mono text-sm" style={{ color: "var(--gold-500)" }}>{t.price}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 text-[11px] font-mono tracking-widest text-slate-500 text-center">
            14-DAY TRIAL · PAYSTACK · CHARGED IN GHS · ACCEPTS VISA · MASTERCARD · MTN MOMO · VODAFONE CASH · AIRTELTIGO · BANK
          </div>
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => navigate("/pricing")}
              data-testid="pricing-cta"
              className="px-6 py-3 font-medium text-sm flex items-center gap-2 transition-all hover:opacity-90"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              View full pricing <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </Section>

        {/* Closing manifesto */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="my-24 lg:my-32 nexus-glass-panel p-10 lg:p-16 text-center relative overflow-hidden"
          data-testid="about-closing"
        >
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D4AF37] opacity-[0.08] blur-[100px]" />
          <div className="label-eyebrow mb-6">// The Pandora Nexus promise</div>
          <p className="font-display text-3xl lg:text-5xl text-white tracking-tight leading-[1.1] max-w-4xl mx-auto">
            When an African institution sits across the table from a minister, a sovereign fund, a regulator or a board —
            the work behind it should be <span style={{ color: "var(--gold-500)" }}>organised, intelligent, defensible
            and executive-grade.</span>
          </p>
          <p className="text-slate-400 text-base lg:text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
            Not held together by spreadsheets and screenshots. Pandora Nexus is the operating layer that makes that
            possible.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate("/pricing")}
              data-testid="closing-pricing-cta"
              className="px-6 py-3 font-medium text-sm flex items-center gap-2 transition-all hover:opacity-90"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              See pricing <ArrowUpRight className="w-4 h-4" />
            </button>
            {!user && (
              <button
                onClick={() => navigate("/login")}
                data-testid="closing-signin-cta"
                className="px-6 py-3 font-medium text-sm border border-white/15 text-slate-200 hover:border-[#D4AF37]/60 hover:text-white transition-all"
              >
                Sign in
              </button>
            )}
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-10" data-testid="about-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/pandora-nexus-mark.png" alt="Pandora Nexus" className="w-7 h-7 object-contain opacity-80" />
            <div className="font-mono text-[10px] tracking-widest text-slate-500">
              © {new Date().getFullYear()} PANDORA MANAGEMENT CONSULT LTD · ACCRA, GHANA
            </div>
          </div>
          <div className="flex items-center gap-5 text-[11px] font-mono tracking-widest text-slate-400">
            <Link to="/about" className="hover:text-[#D4AF37]">ABOUT</Link>
            <Link to="/pricing" className="hover:text-[#D4AF37]">PRICING</Link>
            <a href="https://www.pandoramanagementconsult.com" target="_blank" rel="noreferrer" className="hover:text-[#D4AF37]">
              PANDORAMANAGEMENTCONSULT.COM
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Section = ({ eyebrow, title, children }) => (
  <section className="my-20 lg:my-28">
    <div className="max-w-3xl">
      <div className="label-eyebrow mb-3">{eyebrow}</div>
      <h2 className="font-display text-3xl lg:text-4xl text-white tracking-tight leading-tight">{title}</h2>
    </div>
    {children}
  </section>
);

const Stat = ({ n, label }) => (
  <div>
    <div className="font-display text-2xl" style={{ color: "var(--gold-500)" }}>{n}</div>
    <div className="font-mono text-[9px] tracking-widest text-slate-500 mt-1">{label.toUpperCase()}</div>
  </div>
);
